// apiEndpoint.ts — CORE-45: RU-first API endpoint resolution (native + web).
//
// The prod backend lives at api.agapella.io (origin 204.168.236.62, US). Several
// RU ISPs block/shape that host, so content (events, master profiles, chat REST)
// hangs forever — the native app had NO fallback at all (apiFallback.ts is
// web-only). api-ru.agapella.io is our nginx edge inside Russia that reverse-
// proxies the SAME origin (/api/* + /hubs/*), reachable from RU.
//
// Strategy (owner decision, CORE-45): STRICT RU-FIRST. On startup we probe the
// RU edge's cheap nginx-local /healthz. If it answers, we route everything
// through api-ru; otherwise we use the direct primary. The decision is cached
// (short TTL) so subsequent launches are instant, and re-probed after it expires.
//
// This works on BOTH native and web:
//   - REST: installApiRewrite() patches the global fetch so any api.agapella.io
//     URL is rewritten to api-ru when RU-first is active (one hook, not 270 call
//     sites).
//   - SignalR hubs: resolveHubUrl() rewrites the hub URL at connect time (the
//     hub transports don't all go through fetch).
//
// Non-prod builds (dev api at *.tmless.org) have no RU edge → this is inert.

import { Platform } from 'react-native';
import { storage } from '../utils/storage';
import { applyEnvHost, loadEnvOverride } from '../config/envSwitch';
import { countryFromTimezone } from '../utils/restrictedRegion';
import { getCachedRegion } from './region';
import { trackHostDecision } from './netTelemetry';
import { isRuMode } from './netResilience';

const PRIMARY = (process.env.EXPO_PUBLIC_API_URL || 'https://api.agapella.io').replace(/\/+$/, '');
const RU_EDGE = 'https://api-ru.agapella.io';

// RU-first is meaningful only for the prod origin — the RU edge proxies exactly
// api.agapella.io. A dev/staging build (api.agdev.tmless.org) has no edge.
const RU_ELIGIBLE = /(^|\.)api\.agapella\.io$/.test(hostOf(PRIMARY));

// CORE-723 — awesome-chat-api (the AI companion's REST service) resolves to the
// same address as the main origin, 204.168.236.62, and is shaped from Russia the
// same way: a long answer dies part-way and the connection it died on carries
// nothing afterwards. Short calls survive, which is why the assistant looks
// alive right up until it actually tries to say something. chat-ru.agapella.io
// already terminates inside Russia for the SignalR hub and now carries /api too,
// so the one RU-first decision can cover both. Both values must be present and
// different for this to engage — dev and preview builds set neither, so it is
// inert there.
const CHAT_API_PRIMARY = (process.env.EXPO_PUBLIC_CHAT_API_URL || '').replace(/\/+$/, '');
const CHAT_API_RU_EDGE = (process.env.EXPO_PUBLIC_CHAT_API_RU_PROXY || '').replace(/\/+$/, '');
const CHAT_RU_ELIGIBLE =
  !!CHAT_API_PRIMARY && !!CHAT_API_RU_EDGE && CHAT_API_PRIMARY !== CHAT_API_RU_EDGE;

// CORE-1494 — the messenger (Agape.ChatApi on agape-chat.agapella.io) had NO
// RU route on prod at all. Its REST calls (`dm-conversations`, `unread-summary`,
// `blocked`, channel messages) went to the origin directly, and on some RU
// networks that connection died mid-session: the tester's GET dm-conversations
// reached the origin 20 s after its OPTIONS, the next one never arrived, and the
// chats page spun forever (16.10.2026). The hub survived only thanks to its own
// CORE-407 fallback through chat-ru.
//
// The edge carries the messenger under its own prefix, because `/api/` on
// chat-ru already belongs to the AI service: chat-ru.agapella.io/agape-chat/ ->
// Selectel relay -> agape-chat.agapella.io (docs/infra/chat-ru.agapella.io.nginx.conf).
// Same shape as dev (api.rudev.tmless.org/agape-chat). REST only: the hub keeps
// its CORE-407 path for now.
//
// Prod-only — but NOT by this comparison alone. The dev web job runs `sed` over
// the built JS and swaps https://agape-chat.agapella.io for the dev host,
// literal below included, so in the dev bundle this reads as eligible while the
// RU target above stays the prod edge. The real guard is RU_ELIGIBLE in
// chatRuActive(): the main API address is not rewritten by that job.
const CHAT_HUB_PRIMARY = (process.env.EXPO_PUBLIC_CHAT_HUB_URL || '').replace(/\/+$/, '');
const CHAT_HUB_RU_REST = 'https://chat-ru.agapella.io/agape-chat';
const CHAT_HUB_RU_ELIGIBLE = CHAT_HUB_PRIMARY === 'https://agape-chat.agapella.io';

const CACHE_KEY = 'agape_api_ru_decision';
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 min — re-probe periodically
// CORE-604 — when an RU-likely user is demoted to direct because the edge probe
// definitively failed (edge down), re-probe soon rather than stranding them on
// the shaped/blocked primary for the full TTL.
const CACHE_TTL_DEMOTED_MS = 60 * 1000; // 1 min
const PROBE_TIMEOUT_MS = 2500; // /healthz is nginx-local + tiny; fast in RU

/**
 * CORE-604 — is this visitor RU-likely? Device timezone (VPN-proof — a device
 * setting, not a network fact), UI locale, the app-ru web origin, or a cached
 * GeoIP verdict. Any positive signal makes us default to the RU edge FIRST (even
 * on a direct app.agapella.io load), so a shaped/blocked primary never has to
 * fail before RU routing kicks in. Absent every signal we stay on the fast direct
 * host — non-RU users are never routed through the Moscow edge by default.
 */
export function ruSignal(): boolean {
  try {
    // Loaded from the RU mirror → unambiguously RU.
    if (typeof window !== 'undefined' && /(^|\.)app-ru\.agapella\.io$/.test(window.location?.host || '')) {
      return true;
    }
  } catch { /* no window (native) */ }
  // Server-resolved GeoIP (reflects the VPN exit), when we already have it cached.
  try {
    const region = getCachedRegion();
    if (region === 'RU') return true;
    // CORE-1056 — an AUTHORITATIVE non-RU answer ends the question; do not fall
    // through to the offline heuristics below. The fallbacks exist for the case
    // where the server did not resolve at all (`null`), not to overrule it.
    //
    // Without this the UI-language signal alone routed people who are plainly
    // not in Russia through the Moscow edge: our testers sit in Armenia
    // (`NetworkTelemetryEvents`: `country=AM, ruActive=t, ruSignal=t`) with a
    // Russian-language browser, and that leg cuts a long-lived WebSocket every
    // ~125 s — which is what kept losing one-shot hub frames (CORE-1055).
    // The doc comment above already promises «non-RU users are never routed
    // through the Moscow edge by default»; this is what makes it true.
    //
    // Same trade-off `isRestrictedRegion` already takes: an RU user behind a
    // non-RU VPN exit loses RU-first. Deliberate — the server's answer describes
    // the network the requests actually leave from, which is what routing is about.
    if (region) return false;
  } catch { /* storage unavailable */ }
  // Device timezone — the strongest offline signal (survives VPN + English UI).
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (countryFromTimezone(tz) === 'RU') return true;
  } catch { /* embedded webview without full Intl */ }
  // UI language.
  try {
    const lang = (typeof navigator !== 'undefined' && navigator.language) || '';
    if (lang.toLowerCase().startsWith('ru')) return true;
  } catch { /* no navigator */ }
  return false;
}

let ruActive = false; // current routing decision (mutable, read live by the patch)
let resolving: Promise<boolean> | null = null;

// CORE-493 — the startup RU-first decision. Until it settles, a primary-host
// request (e.g. the very first /api/Auth/login) would otherwise be sent to the
// shaped/blocked primary before RU-first activates. `initPromise` lets the fetch
// patch wait for the decision for primary-host URLs only; `ready` short-circuits
// that wait once decided so steady-state requests stay synchronous.
let initPromise: Promise<void> | null = null;
let ready = false;

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return '';
  }
}

/** Whether requests are currently being routed through the RU edge. */
export function isRuActive(): boolean {
  return ruActive;
}

/**
 * Rewrite a primary-host URL to the RU edge when RU-first is active — and, since
 * CORE-723, a chat-api URL to the chat RU edge on the same decision. Any other
 * URL (already-RU, dev host, S3, absolute third-party) is returned unchanged, so
 * callers can safely wrap every URL. Used by both the fetch patch and the hub
 * URL builders.
 */
export function ruUrl(url: string): string {
  if (!url) return url;
  if (ruActive && url.startsWith(PRIMARY)) return RU_EDGE + url.slice(PRIMARY.length);
  if (!chatRuActive()) return url;
  if (CHAT_RU_ELIGIBLE && url.startsWith(CHAT_API_PRIMARY)) {
    return CHAT_API_RU_EDGE + url.slice(CHAT_API_PRIMARY.length);
  }
  if (CHAT_HUB_RU_ELIGIBLE && url.startsWith(`${CHAT_HUB_PRIMARY}/api/`)) {
    return CHAT_HUB_RU_REST + url.slice(CHAT_HUB_PRIMARY.length);
  }
  return url;
}

/**
 * CORE-1494 — the chat legs follow the web breaker's verdict as well as
 * RU-first.
 *
 * RU-first stays off for a visitor whose cached GeoIP region is non-RU — by
 * design (CORE-1056), and that region is the VPN exit for up to 24 h. The tester
 * who compared «with VPN» and «without VPN» in one browser carried `NL` for a day
 * after switching the VPN off: `host_decision: direct, ruSignal=false,
 * probe=ok`. The main API was rescued by the web breaker (`apiFallback`, sticky
 * on api-ru), but the AI service and the messenger have no breaker of their own
 * and kept going direct.
 *
 * `isRuMode()` is the breaker's proof that the direct origin is dead FROM THIS
 * NETWORK — set only after REST to it actually failed (CORE-449). That is
 * stronger than a GeoIP verdict and does not reopen CORE-1056: a visitor whose
 * direct path works never trips the breaker, so it never routes them through
 * Moscow.
 */
/**
 * CORE-1494 — the same verdict for the chat hub's sockets.
 *
 * The hub chose the RU proxy first only under RU-first (`isRuActive()`). The
 * tester with a stale VPN region had RU-first off and the breaker on: the hub
 * kept connecting DIRECT, the socket opened fine and died 30–37 s later
 * (negotiate 12:06:24 -> close 12:07:01, 12:08:02 -> 12:08:33, …, 16.10.2026),
 * SignalR reconnected to the same direct address, and the thread waiting for
 * its history spun forever. The CORE-407 fallback never fired: it covers a
 * connect that fails, not a socket that opens and then goes quiet.
 */
export function isChatRuActive(): boolean {
  return chatRuActive();
}

function chatRuActive(): boolean {
  // RU_ELIGIBLE first: only a bundle whose main API is the prod origin may send
  // the chat legs to the prod RU edge. On dev and the stand the chat hosts can
  // look like prod after the build's host rewrite (see CHAT_HUB_RU_ELIGIBLE), and
  // a dev token must never land on the prod edge.
  return RU_ELIGIBLE && (ruActive || isRuMode());
}

/**
 * CORE-1528 — RU-адрес для прямого, независимо от текущего решения: нужен
 * нативной страховке (`nativeApiFallback`), которая повторяет зависший прямой
 * запрос через Москву ещё ДО того, как RU-first включён. `host` говорит, чей
 * это адрес: у основного API и у REST ИИ разные правила повтора. null — адрес
 * не прямой прод (dev-хост, уже RU-эдж, чужой сервер): повторять некуда.
 */
export function ruEdgeFor(url: string): { url: string; host: 'api' | 'chat' } | null {
  if (!url) return null;
  if (RU_ELIGIBLE && url.startsWith(PRIMARY)) return { url: RU_EDGE + url.slice(PRIMARY.length), host: 'api' };
  if (CHAT_RU_ELIGIBLE && url.startsWith(CHAT_API_PRIMARY)) {
    return { url: CHAT_API_RU_EDGE + url.slice(CHAT_API_PRIMARY.length), host: 'chat' };
  }
  return null;
}

/**
 * CORE-1528 — натив: прямой путь доказанно завис, а RU-эдж ответил. Включает
 * RU-first так же, как его включила бы проба для RU-пользователя: дальше REST,
 * хабы и REST ИИ идут через Москву, решение кешируется обычным ключом, и
 * следующий запуск начинается уже с него. Веб сюда не ходит — у него свой
 * брейкер в apiFallback.
 */
export function promoteRuFirst(): void {
  if (!RU_ELIGIBLE || ruActive) return;
  ruActive = true;
  void storage.setJSON(CACHE_KEY, { ru: true, ts: Date.now() }).catch(() => undefined);
}

/**
 * The full outgoing rewrite: env-switch first (CORE-413 — dev/prod host swap in
 * non-prod builds), then RU-first (CORE-45). Env picks the base host; RU-first
 * then reroutes the prod host to api-ru when active. Used by the fetch patch and
 * hub URL builders so both paths get the same treatment.
 */
function rewriteOutgoing(url: string): string {
  return ruUrl(applyEnvHost(url));
}

/** Rewrite for SignalR hub URL construction (live / watch-party / chat hubs). */
export function resolveHubUrl(url: string): string {
  return rewriteOutgoing(url);
}

/**
 * CORE-1882 — the hub URL on the direct host, whatever RU-first says (the env
 * switch still applies). For a hub that failed to start through the RU edge.
 */
export function directHubUrl(url: string): string {
  return applyEnvHost(url);
}

/** CORE-1882 — the hub URL on the RU edge, or null where there is no RU edge (dev, stand). */
export function ruEdgeHubUrl(url: string): string | null {
  const direct = applyEnvHost(url);
  return RU_ELIGIBLE && direct.startsWith(PRIMARY) ? RU_EDGE + direct.slice(PRIMARY.length) : null;
}

/** CORE-1882 — does this URL go through the RU edge. */
export function isRuEdgeUrl(url: string): boolean {
  return url.startsWith(RU_EDGE);
}

// CORE-604 — a slow/timed-out probe is NOT the same as a dead edge. For an
// RU-likely user, `hardFail` (a network-level failure — the edge is truly
// unreachable) is the only signal strong enough to demote them back to the
// direct host; a timeout just means the RU network is slow, and the direct host
// is likely shaped anyway, so we keep RU-first.
type ProbeResult = { ok: boolean; hardFail: boolean };

async function probeRuEdge(): Promise<ProbeResult> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT_MS);
  try {
    // /healthz is served by the edge nginx itself (doesn't touch the origin),
    // so a 200 proves the RU box is reachable from this client.
    const res = await fetch(`${RU_EDGE}/healthz`, { method: 'GET', signal: ctrl.signal });
    return { ok: res.ok, hardFail: false };
  } catch (err) {
    // AbortError = our own timeout (slow, inconclusive); TypeError = DNS/TCP/TLS
    // failure = the edge is genuinely unreachable (hard fail).
    const timedOut = err instanceof Error && err.name === 'AbortError';
    return { ok: false, hardFail: !timedOut };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Decide (and cache) whether to route through the RU edge. Idempotent per call
 * cycle. Returns the decision. Safe to call repeatedly — a fresh cache short-
 * circuits the network probe. Call once at startup; the fetch patch and hub
 * builders read the live decision thereafter.
 */
export async function resolveApiEndpoint(): Promise<boolean> {
  if (!RU_ELIGIBLE) return false; // dev / non-prod — no RU edge to prefer
  if (resolving) return resolving;

  resolving = (async () => {
    const signal = ruSignal();
    try {
      const cached = await storage.getJSON<{ ru: boolean; ts: number }>(CACHE_KEY);
      if (cached && typeof cached.ts === 'number') {
        const age = Date.now() - cached.ts;
        // A cached "direct" verdict for an RU-likely user expires fast (CORE-604)
        // so a one-off edge blip doesn't strand them on the shaped primary.
        const ttl = (!cached.ru && signal) ? CACHE_TTL_DEMOTED_MS : CACHE_TTL_MS;
        if (age < ttl) {
          ruActive = !!cached.ru;
          trackHostDecision({ ruActive, ruSignal: signal, decisionSource: 'cache' });
          return ruActive;
        }
      }
    } catch {
      // storage unavailable — fall through to a live probe
    }

    const probe = await probeRuEdge();
    // CORE-604 — RU-first when RU-likely: an RU-signal visitor defaults to the RU
    // edge and is demoted to the direct host ONLY when the probe hard-fails (edge
    // truly down); a slow/timed-out probe keeps them on RU (the direct primary is
    // the shaped host we're avoiding). A non-signal visitor stays on the fast
    // direct host and is never routed through Moscow by default.
    const ru = signal ? !probe.hardFail : false;
    ruActive = ru;
    trackHostDecision({
      ruActive: ru,
      ruSignal: signal,
      decisionSource: 'probe',
      probeOutcome: probe.ok ? 'ok' : (probe.hardFail ? 'hard_fail' : 'timeout'),
    });
    try {
      await storage.setJSON(CACHE_KEY, { ru, ts: Date.now() });
    } catch {
      // best-effort cache; the in-memory decision still holds for this session
    }
    return ru;
  })();

  try {
    return await resolving;
  } finally {
    resolving = null;
  }
}

/**
 * Patch the global fetch so an api.agapella.io URL is transparently sent to the
 * RU edge whenever RU-first is active. Cross-platform (native + web). On web this
 * wraps whatever apiFallback already installed — install this one AFTER it, so a
 * rewritten (api-ru) URL passes straight through apiFallback's primary check.
 */
export function installApiRewrite(): void {
  const g = globalThis as any;
  if (g.__apiRuRewriteInstalled) return;
  if (typeof g.fetch !== 'function') return;
  g.__apiRuRewriteInstalled = true;

  const orig: typeof fetch = g.fetch.bind(g);

  g.fetch = async function ruPatchedFetch(input: any, init?: any): Promise<Response> {
    try {
      // CORE-493 — close the RU-first race: if a primary-host request fires while
      // the startup decision is still in flight, wait for it so the first calls
      // (login) route to api-ru directly instead of hitting the shaped/blocked
      // primary first. Primary-host URLs only — the probe's own api-ru/healthz
      // and every other host skip this, so there's no self-deadlock and no added
      // latency once `ready`.
      // CORE-604 — once RU is already active (optimistically, for an RU-likely
      // visitor) a primary-host URL rewrites to api-ru immediately, so there's no
      // reason to await the probe. We only block the first primary-host calls when
      // we haven't yet decided to route through RU.
      if (RU_ELIGIBLE && !ready && !ruActive && initPromise) {
        const raw = typeof input === 'string' ? input
          : (typeof URL !== 'undefined' && input instanceof URL) ? input.toString()
          : (input && typeof input === 'object' && typeof input.url === 'string') ? input.url
          : '';
        if (raw && applyEnvHost(raw).startsWith(PRIMARY)) {
          try { await initPromise; } catch { /* fall through to current decision */ }
        }
      }

      // rewriteOutgoing = env-switch (CORE-413) then RU-first (CORE-45); it
      // returns the URL unchanged when neither applies, so run it unconditionally.
      if (typeof input === 'string') {
        const r = rewriteOutgoing(input);
        if (r !== input) return orig(r, init);
      } else if (typeof URL !== 'undefined' && input instanceof URL) {
        const s = input.toString();
        const r = rewriteOutgoing(s);
        if (r !== s) return orig(r, init);
      } else if (input && typeof input === 'object' && typeof input.url === 'string') {
        const r = rewriteOutgoing(input.url);
        if (r !== input.url) return orig(new Request(r, input as Request), init);
      }
    } catch {
      // never let the routing layer break a request — fall through to primary
    }
    return orig(input, init);
  };
}

/** Fire the resolver at startup (non-blocking) then keep the decision fresh. */
export function initApiEndpoint(): void {
  installApiRewrite();
  // CORE-604 — optimistic RU-first: an RU-likely visitor routes to the RU edge
  // from the very first call (before the probe settles), so a shaped/blocked
  // primary never has to fail first. The probe below only DEMOTES (on a hard
  // edge failure), never gates the initial RU decision for these users.
  if (RU_ELIGIBLE && !ruActive && ruSignal()) ruActive = true;
  // Load the env-switch override (CORE-413) FIRST so the base env is known, then
  // run the RU-first probe within that env. Both are read live by the patch.
  // CORE-493 — hold the init promise so the fetch patch can await it for the
  // first primary-host calls; flip `ready` when settled so later calls don't.
  ready = false;
  initPromise = (async () => {
    try {
      await loadEnvOverride();
      await resolveApiEndpoint();
    } catch {
      // best-effort — a failed probe just leaves ruActive=false (direct primary)
    } finally {
      ready = true;
    }
  })();
}

/** Visible-for-testing reset. */
export function _resetApiEndpoint(): void {
  ruActive = false;
  resolving = null;
  initPromise = null;
  ready = false;
  const g = globalThis as any;
  g.__apiRuRewriteInstalled = false;
}

/** Visible-for-testing setter. */
export function _setRuActiveForTest(v: boolean): void {
  ruActive = v;
}

// Silence unused-import lints on web-only builds; Platform kept for parity with
// sibling services and any future native-specific probing.
void Platform;
